import React, { useCallback } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { ReactComponent as NotesSolidIcon } from 'resources/images/notes-solid.svg';
import { addAlertAction } from 'app/store/Alerts/slice';
import { AppDispatch, useAppSelector } from 'app/store';
import { SUPPORTED_AUDIO_FILES } from 'app/components/Song/Audio/AudioList/constants';
import {
  Button,
  Flex,
  Icon,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SystemStyleObject,
  Text,
} from '@chakra-ui/react';
import { getStorage } from 'app/store/Storage/selectors';
import { getProcessingSongAudioList } from 'app/store/SongList/selectors';
import { uploadAudioBlockAction } from 'app/store/SongList/slice';
import { useStorageFeatureFlag } from 'featureFlags';
import { AudioFormValidationProperties } from 'types/audio';
import { getFileUploadErrorMessage } from 'app/components/Song/Audio/AudioDragAndDropModal/utils';

const baseSx: SystemStyleObject = {
  py: [8, 12],
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderStyle: 'dashed',
  borderWidth: '1px',
  borderRadius: 6,
  borderColor: 'M.40',
  bg: 'surface2',
  outline: 'none',
  width: '100%',
  maxWidth: '1200px',
  maxHeight: 'calc(100vh - 115px)',
  height: 500,
  overflow: 'auto',
  '@media screen and (max-height: 700px)': {
    marginTop: '90px',
  },
};

const closeBtnSx: SystemStyleObject = {
  size: 'xl',
  height: 12,
  width: 12,
  right: 10,
  top: 10,
  bg: 'M.30',
  color: 'textSubdued',
  _hover: {
    color: 'textBase',
  },
  borderRadius: '50%',
  outline: 'none',
};

const activeSx: SystemStyleObject = {
  borderColor: 'B.0',
  borderStyle: 'solid',
};

const acceptSx: SystemStyleObject = {
  borderColor: 'B.0',
  borderStyle: 'solid',
};

const rejectSx: SystemStyleObject = {
  borderColor: 'borderWarning',
  borderStyle: 'solid',
};

export interface AudioDragAndDropModalProps {
  isOpen: boolean;
  close: () => void;
  setAudioFiles: (audioFiles: FileList) => void;
  setShowAddAudioDialog: (show: boolean) => void;
  setShowStorageOverLimitDialog: () => void;
  setDnDModal: (show: boolean) => void;
  dispatch: AppDispatch;
  audioFormValidation?: AudioFormValidationProperties;
}

export const AudioDragAndDropModal: React.FC<AudioDragAndDropModalProps> = ({
  isOpen,
  close,
  setAudioFiles,
  dispatch,
  setShowAddAudioDialog,
  setShowStorageOverLimitDialog,
  setDnDModal,
  audioFormValidation,
}) => {
  const storage = useAppSelector(getStorage);
  const processingAudios = useAppSelector(getProcessingSongAudioList);
  const { storageFeatureEnabled } = useStorageFeatureFlag();

  const checkStorageLimit = useCallback(
    (files) => {
      // prevent restricting
      if (!storage?.enforceLimit || !storageFeatureEnabled) {
        return true;
      }
      const sumOfFileSizes = files.reduce(
        (accumulator: number, currentValue) =>
          accumulator + (currentValue.size || currentValue.localFileSize),
        0
      );

      if (sumOfFileSizes >= (storage?.freeBytes || 0)) {
        dispatch(uploadAudioBlockAction());
        setShowStorageOverLimitDialog();
        return false;
      }

      return true;
    },
    [
      storage?.enforceLimit,
      storage?.freeBytes,
      storageFeatureEnabled,
      dispatch,
      setShowStorageOverLimitDialog,
    ]
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({
    accept: SUPPORTED_AUDIO_FILES,
    noClick: true,
    onDropRejected: (rejections: FileRejection[]) => {
      rejections.forEach((rejection) => {
        rejection.errors.forEach((error) => {
          if (error.code === 'file-invalid-type') {
            dispatch(
              addAlertAction({
                message: `${rejection.file.name} has unsupported file type, please try another type.`,
              })
            );
          }
        });
      });
    },
    onDropAccepted: (acceptedFiles) => {
      if (checkStorageLimit([...acceptedFiles, ...processingAudios])) {
        const filteredFiles = acceptedFiles.filter((file) => {
          const errorMessage = getFileUploadErrorMessage(
            file,
            audioFormValidation
          );

          if (errorMessage) {
            dispatch(addAlertAction({ message: errorMessage }));
            return false;
          }

          return true;
        });

        if (!filteredFiles.length) {
          return;
        }

        setAudioFiles((filteredFiles as unknown) as FileList);
        setShowAddAudioDialog(true);
        setDnDModal(false);
      } else {
        setDnDModal(false);
      }
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={close} isCentered size="full">
      <ModalOverlay left="-1px" />
      <ModalContent justifyContent="center" alignItems="center" px={8}>
        <ModalCloseButton sx={closeBtnSx} />
        <Flex
          sx={{
            ...baseSx,
            ...(isDragActive ? activeSx : {}),
            ...(isDragAccept ? acceptSx : {}),
            ...(isDragReject ? rejectSx : {}),
          }}
          {...getRootProps()}
          data-testid="dragAndDrop"
        >
          <input {...getInputProps()} />
          <Icon
            as={NotesSolidIcon}
            flexShrink={0}
            height="96px"
            width="96px"
            color="P.50"
          />
          <Text
            fontSize="lg"
            fontWeight={500}
            color="textBase"
            mt={6}
            textAlign="center"
          >
            Drag and drop your audio or MIDI files here
          </Text>
          <Text fontSize="md" color="textSubdued" mt={0.5}>
            or select a file from you file browser.
          </Text>
          <Button mt={6} onClick={open} data-testid="browser-button-test">
            Browse
          </Button>
        </Flex>
      </ModalContent>
    </Modal>
  );
};
